import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createOccasion, updateOccasion, fetchOccasionById } from './occasionSlice';
import { fetchTrends } from '../trend/trendSlice';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import log from '../../logger';
import ReactForm from '../../components/ReactForm';
import ReactDropdown from '../../components/ReactDropdown';
import { paths } from '../../routes/helpers';
import { getImageUrl, toLocalUploadPath } from '../../utils/urlUtils';
import ImageGallery from '../../components/ImageGallery';

const OccasionCreateEdit = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { occasionId } = useParams();
  const loading = useSelector((state) => state.occasion.loading);
  const error = useSelector((state) => state.occasion.error);
  const occasion = useSelector((state) => 
    occasionId ? state.occasion.items.find(b => b._id === occasionId) : null
  );
  const trends = useSelector((state) => state.trend.items);
  
  const [imageFiles, setImageFiles] = useState([]);
  const [existingImage, setExistingImage] = useState('');
  const [selectedTrends, setSelectedTrends] = useState([]);
  const [trendsLoading, setTrendsLoading] = useState(false);

  useEffect(() => {
    if (occasionId) {
      dispatch(fetchOccasionById(occasionId));
    }
    // Fetch trends for dropdown
    setTrendsLoading(true);
    dispatch(fetchTrends({ limit: 100 })).finally(() => setTrendsLoading(false));
  }, [occasionId, dispatch]);

  useEffect(() => {
    if (occasion) {
      if (occasion.image) {
        setExistingImage(getImageUrl(occasion.image));
      }
      // Set selected trends from occasion
      if (occasion.trends) {
        const trendObjects = Array.isArray(occasion.trends) 
          ? occasion.trends.map(trend => {
              if (typeof trend === 'object') {
                return {
                  value: trend._id,
                  label: trend.label
                };
              } else if (typeof trend === 'string') {
                // Find the trend object from the trends array
                const trendObj = trends.find(t => t._id === trend);
                return trendObj ? {
                  value: trendObj._id,
                  label: trendObj.label
                } : null;
              }
              return null;
            }).filter(Boolean)
          : [];
        setSelectedTrends(trendObjects);
      }
    }
  }, [occasion, trends]);

  const handleImageChange = (event) => {
    const files = Array.from(event.target.files);
    setImageFiles(files);
  };

  const handleRemoveImage = (index) => {
    setImageFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  const handleRemoveExistingImage = () => {
    setExistingImage('');
  };

  const handleSubmit = async (formData) => {
    try {
      const processedData = new FormData();
      processedData.append('label', formData.label);
      processedData.append('status', parseInt(formData.status));
      
      // Add trends array (required)
      if (selectedTrends.length === 0) {
        toast.error('Please select at least one trend');
        return;
      }

      // Ensure trends is sent as an array
      selectedTrends.forEach(trend => {
        processedData.append('trends[]', trend.value);
      });
      
      if (imageFiles.length > 0) {
        processedData.append('image', imageFiles[0]);
      } else if (existingImage && occasionId) {
        // Keep existing image
        processedData.append('existingImage', toLocalUploadPath(existingImage));
      }

      if (occasionId) {
        await dispatch(updateOccasion({ id: occasionId, formData: processedData })).unwrap();
        toast.success('Occasion updated successfully!');
        log.info('Occasion updated:', formData.label);
      } else {
        await dispatch(createOccasion(processedData)).unwrap();
        toast.success('Occasion created successfully!');
        log.info('Occasion created:', formData.label);
      }
      navigate(paths.private.occasion.list());
    } catch (err) {
      toast.error(err.message || 'An error occurred');
      log.error('Error creating/updating occasion:', err);
    }
  };

  const trendsOptions = trends.map(trend => ({
    value: trend._id,
    label: trend.label
  }));

  const fields = [
    {
      name: 'label',
      label: 'Label',
      type: 'text',
      required: true,
      placeholder: 'Enter occasion label'
    },
    {
      name: 'trends',
      label: 'Trends',
      type: 'dropdown',
      required: true,
      component: (
        <ReactDropdown
          options={trendsOptions}
          value={selectedTrends}
          onChange={setSelectedTrends}
          isMulti={true}
          required={true}
          isLoading={trendsLoading}
          placeholder="Select Trends"
        />
      )
    },
    {
      name: 'image',
      label: 'Image',
      type: 'file',
      accept: 'image/*',
      required: !occasionId || (!existingImage && imageFiles.length === 0),
      onChange: handleImageChange,
      preview: (
        <ImageGallery 
          existingImages={imageFiles.length > 0 ? [] : existingImage ? [existingImage] : []}
          imageFiles={imageFiles}
          onRemoveExisting={handleRemoveExistingImage}
          onRemoveNew={handleRemoveImage}
          loading={loading && occasion?.image && !existingImage}
        />
      )
    },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      required: true,
      defaultValue: "0",
      options: [
        { value: "0", label: 'Inactive' },
        { value: "1", label: 'Active' },
        { value: "2", label: 'Suspended' }
      ]
    }
  ];

  const defaultValues = occasion ? {
    label: occasion.label,
    image: [],
    status: occasion.status.toString(),
    // Trends are handled via the selectedTrends state
  } : {
    label: '',
    image: [],
    status: "1",
    // Trends are handled via the selectedTrends state
  };

  log.info('Default values:', defaultValues);

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-4">
        {occasionId ? 'Edit Occasion' : 'Create Occasion'}
      </h2>
      <ReactForm
        fields={fields}
        onSubmit={handleSubmit}
        loading={loading}
        error={error}
        defaultValues={defaultValues}
        submitText={occasionId ? 'Update Occasion' : 'Create Occasion'}
      />
    </div>
  );
};

export default OccasionCreateEdit; 